import { useState, useRef } from 'react';
import { Phone, Mail, MapPin, ShieldCheck, Check, Loader2, Send } from 'lucide-react';
import { SITE_INFO } from '../data';

export default function ContactSection({ title = "Indhent et uforpligtende tilbud", subtitle = "Har du et konkret byggeprojekt eller brug for fagmæssig rådgivning? Send os en besked eller ring direkte." }) {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    project_type: 'Tagarbejde',
    description: '',
    website_url: '' // Mandatory Honeypot
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Indtast venligst dit navn';
    if (!formData.phone.trim()) newErrors.phone = 'Indtast venligst dit telefonnummer';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Indtast en gyldig e-mailadresse';
    if (!formData.description.trim()) newErrors.description = 'Skriv venligst en beskrivelse af dit projekt';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!validate()) return;

    setLoading(true);

    try {
      const data = new FormData(formRef.current);
      
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: data,
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          project_type: 'Tagarbejde',
          description: '',
          website_url: ''
        });
      } else {
        const resJson = await response.json().catch(() => ({}));
        setErrorMessage(resJson.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Netværksfejl. Kontroller din forbindelse og prøv igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt-sektion" className="py-20 bg-stone-50 text-stone-900 border-t border-stone-200 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Direct Info & Reassurance */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-stone-900 tracking-tight uppercase">
                {title}
              </h2>
              <p className="text-stone-600 text-base mt-4 leading-relaxed">
                {subtitle}
              </p>
            </div>

            <div className="space-y-6 pt-2">
              <div className="flex items-start gap-4">
                <div className="shrink-0 pt-0.5">
                  <Phone className="w-5 h-5 text-stone-800" aria-hidden="true" />
                </div>
                <div>
                  <a 
                    href={`tel:${SITE_INFO.phone.replace(/\s+/g, '')}`} 
                    className="text-lg font-heading font-bold text-stone-900 hover:text-amber-700 transition-colors whitespace-nowrap inline-block"
                  >
                    {SITE_INFO.phone}
                  </a>
                  <p className="text-xs text-stone-500 mt-0.5">Man - Fre: 07.00 - 16.00</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="shrink-0 pt-0.5">
                  <Mail className="w-5 h-5 text-stone-800" aria-hidden="true" />
                </div>
                <div>
                  <a 
                    href={`mailto:${SITE_INFO.email}`} 
                    className="text-base font-medium text-stone-900 hover:text-amber-700 transition-colors"
                  >
                    {SITE_INFO.email}
                  </a>
                  <p className="text-xs text-stone-500 mt-0.5">Vi besvarer inden for 24 timer</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="shrink-0 pt-0.5">
                  <MapPin className="w-5 h-5 text-stone-800" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-900">{SITE_INFO.address}</p>
                  <p className="text-xs text-stone-500 mt-0.5">CVR: {SITE_INFO.cvr}</p>
                </div>
              </div>
            </div>

            {/* Trust badge */}
            <div className="bg-white p-6 border border-stone-200 shadow-sm flex items-center gap-4">
              <ShieldCheck className="w-8 h-8 text-amber-600 shrink-0" aria-hidden="true" />
              <div>
                <h4 className="font-heading font-bold text-stone-900 text-sm">Garanteret Fagligt Håndværk</h4>
                <p className="text-stone-600 text-xs mt-1">Gennemskueligt tilbud uden skjulte ekstraregninger.</p>
              </div>
            </div>
          </div>

          {/* Right Column: Webform */}
          <div className="lg:col-span-7 bg-white p-8 sm:p-10 border border-stone-200 shadow-xl">
            {success ? (
              <div className="bg-amber-50 border border-amber-200 p-8 text-center space-y-4 animate-in fade-in duration-300">
                <div className="w-16 h-16 bg-amber-600 text-white flex items-center justify-center mx-auto text-2xl font-bold">
                  <Check className="w-8 h-8 text-white" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-stone-900">Tak for din henvendelse!</h3>
                <p className="text-stone-700 text-sm leading-relaxed max-w-md mx-auto">
                  Vi har modtaget dine oplysninger og vender tilbage med en opfølgning eller et uforpligtende tilbud hurtigst muligt.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-4 bg-stone-900 hover:bg-stone-800 text-white text-xs font-semibold px-5 py-2.5 transition-colors cursor-pointer"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" noValidate>
                
                {/* MANDATORY Honeypot (sr-only + absolute) */}
                <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website_url">Lad dette felt være tomt</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Navn */}
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
                      Navn <span className="text-amber-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Dit fulde navn"
                      className={`w-full bg-stone-50 border ${
                        errors.name ? 'border-red-500' : 'border-stone-300 focus:border-amber-600'
                      } px-4 py-3 text-stone-900 text-sm focus:outline-none transition-colors`}
                    />
                    {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* Telefonnummer */}
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
                      Telefon <span className="text-amber-600">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="f.eks. 20 30 40 50"
                      className={`w-full bg-stone-50 border ${
                        errors.phone ? 'border-red-500' : 'border-stone-300 focus:border-amber-600'
                      } px-4 py-3 text-stone-900 text-sm focus:outline-none transition-colors`}
                    />
                    {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
                      E-mail <span className="text-amber-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="din@email.dk"
                      className={`w-full bg-stone-50 border ${
                        errors.email ? 'border-red-500' : 'border-stone-300 focus:border-amber-600'
                      } px-4 py-3 text-stone-900 text-sm focus:outline-none transition-colors`}
                    />
                    {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Projekt Type */}
                  <div>
                    <label htmlFor="project_type" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
                      Ydelse / Emne
                    </label>
                    <select
                      id="project_type"
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleChange}
                      className="w-full bg-stone-50 border border-stone-300 focus:border-amber-600 px-4 py-3 text-stone-900 text-sm focus:outline-none transition-colors cursor-pointer"
                    >
                      <option value="Tagarbejde">Tagarbejde & Tagrenovering</option>
                      <option value="Projektudvikling">Projektudvikling & Totalentreprise</option>
                      <option value="Døre og vinduer">Døre & Vinduer</option>
                      <option value="Specialløsninger">Specialløsninger</option>
                      <option value="Træterrasse">Træterrasser & Uderum</option>
                      <option value="Renovering">Renovering & Ombygning</option>
                      <option value="Køkken">Køkken & Bad</option>
                      <option value="Andet">Andet Tømrerarbejde</option>
                    </select>
                  </div>
                </div>

                {/* Beskrivelse */}
                <div>
                  <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
                    Projektbeskrivelse <span className="text-amber-600">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows="4"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Beskriv opgavens omfang, ønsker til materialer eller tidsramme..."
                    className={`w-full bg-stone-50 border ${
                      errors.description ? 'border-red-500' : 'border-stone-300 focus:border-amber-600'
                    } px-4 py-3 text-stone-900 text-sm focus:outline-none transition-colors`}
                  ></textarea>
                  {errors.description && <p className="text-red-600 text-xs mt-1">{errors.description}</p>}
                </div>

                {/* File Attachment */}
                <div>
                  <label htmlFor="files" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
                    Vedhæft tegninger / billeder (valgfrit)
                  </label>
                  <input
                    type="file"
                    id="files"
                    name="files"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                    className="w-full text-xs text-stone-600 file:mr-4 file:py-2.5 file:px-4 file:border-0 file:text-xs file:font-semibold file:bg-stone-200 file:text-stone-800 hover:file:bg-stone-300 cursor-pointer"
                  />
                  <span className="text-[11px] text-stone-500 mt-1 block">Max 5 filer (PDF, PNG, JPG, WEBP - max 10MB pr. fil)</span>
                </div>

                {errorMessage && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-xs">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-500 text-white font-heading font-bold text-xs sm:text-base py-2.5 sm:py-3.5 shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      <span>Afsender henvendelse...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Henvendelse & Indhent Tilbud</span>
                      <Send className="w-4 h-4" aria-hidden="true" />
                    </>
                  )}
                </button>

                <p className="text-[11px] text-stone-500 text-center">
                  Uforpligtende rådgivning. Vi behandler dine oplysninger med fuld fortrolighed.
                </p>

              </form>
            )}
          </div>

        </div>

      </div>
    </section>
  );
}
